import React, { useState } from 'react';

import { Avatar, AvatarProps, Box, Loader, Menu, Modal, UnstyledButton } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { IconEdit, IconTrash, IconUpload } from '@tabler/icons-react';
import { ImageDropzone } from 'components/ImageDropzone';
import { useTranslation } from 'react-i18next';
import { MAX_AVATAR_SIZE } from 'shared/constants/general-constants';

interface EditAvatarProps extends AvatarProps {
  placeholder?: string;
  onFileDrop?: (file: File) => void;
  onRemove?: () => void;
  disabled?: boolean;
  loading?: boolean;
}

export function EditAvatar({ placeholder, onFileDrop, onRemove, disabled, loading, ...avatarProps }: EditAvatarProps) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);

  const isDisabled = disabled || loading;
  const editIconPos = `calc(50% - 10px)`; // 10px is half of the icon size (default size is 20px)
  const hasAvatarSrc = avatarProps.src && avatarProps.src !== '';

  return (
    <>
      <Menu position="bottom" disabled={isDisabled}>
        <Menu.Target>
          <UnstyledButton>
            <Box
              onMouseEnter={() => !isDisabled && setHover(true)}
              onMouseLeave={() => setHover(false)}
              sx={{
                cursor: isDisabled ? 'default' : 'pointer'
              }}>
              <Box
                sx={{
                  position: 'relative'
                }}>
                <Avatar
                  sx={{
                    opacity: hover ? 0.5 : disabled ? 0.7 : 1
                  }}
                  {...avatarProps}
                  src={hasAvatarSrc ? avatarProps.src : undefined}>
                  {loading ? null : placeholder}
                </Avatar>
                {loading ? (
                  <Box style={{ position: 'absolute', top: editIconPos, left: editIconPos }}>
                    <Loader size="sm" />
                  </Box>
                ) : (
                  hover && (
                    <Box style={{ position: 'absolute', top: editIconPos, left: editIconPos, opacity: '1 !important' }}>
                      <IconEdit />
                    </Box>
                  )
                )}
              </Box>
            </Box>
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item icon={<IconUpload />} onClick={() => setModalOpened(true)}>
            {t('APP.ACTIONS.UPLOAD')}
          </Menu.Item>

          <Menu.Item color="red" icon={<IconTrash />} onClick={() => onRemove?.()}>
            {t('APP.ACTIONS.REMOVE')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <Modal maw={250} opened={modalOpened} onClose={() => setModalOpened(false)} withCloseButton={false}>
        <ImageDropzone
          maxFileSizeBytes={MAX_AVATAR_SIZE}
          onFileDrop={(file: FileWithPath) => {
            onFileDrop?.(file);
            setModalOpened(false);
          }}
        />
      </Modal>
    </>
  );
}
